import React, { useEffect, useState } from 'react'
import { useUserContext } from '../../../../_context/UserContext';
import { submitUser } from '../../../../_utils/resourceAPI';
import EmptyUser from '../../../../_resources/JsonData/EmptyUser.json';
import SuccessfullSubmission from '../../Notifications/SuccessfullSubmission';

interface UserBasicDetaisProps {
  nameValidator: (value: string, isRequired: boolean, inputName: string) => string | null;
  userNameValidator: (value: string, isRequired: boolean, inputName: string) => string | null;
  emailValidator: (value: string, isRequired: boolean, inputName: string) => string | null;
  passwordValidator: (value: string, isRequired: boolean, inputName: string) => string | null;
  selectValidator: (value: string, isRequired: boolean, labelName: string) => string | null;
}

const EyeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

export const UserBasicDetails: React.FC<UserBasicDetaisProps> = ({
  nameValidator, userNameValidator, emailValidator, passwordValidator, selectValidator
}) => {
  const { newUser, setNewUser } = useUserContext();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const resetForm = () => setNewUser(EmptyUser);

  const submitUserHandler = (e: React.FormEvent) => {
    e.preventDefault();
    submitUser(newUser)
      .then(() => { setShowSuccessModal(true); resetForm(); })
      .catch(() => console.log("Something went wrong"));
  };

  useEffect(() => { console.log("New User:", newUser); }, [newUser]);

  const isFormValid = () =>
    !nameValidator(newUser.name, true, "") &&
    !userNameValidator(newUser.username, true, "") &&
    !emailValidator(newUser.email, true, "") &&
    !passwordValidator(newUser.password, true, "") &&
    !selectValidator(newUser.role, true, "");

  const fieldError = (validator: Function, value: string) => {
    const err = validator(value, true, "");
    return value && err ? <div className="au-field-error">{err}</div> : null;
  };

  return (
    <form onSubmit={submitUserHandler} noValidate>

      <div className="au-field">
        <label htmlFor="SysUserName">Full Name</label>
        <input
          id="SysUserName"
          type="text"
          placeholder="e.g. Jane Smith"
          value={newUser.name}
          onChange={e => setNewUser({ ...newUser, name: e.target.value })}
          className={nameValidator(newUser.name, true, "") && newUser.name ? "is-invalid" : ""}
        />
        {fieldError(nameValidator, newUser.name)}
      </div>

      <div className="au-field">
        <label htmlFor="SysUserUserName">Username</label>
        <input
          id="SysUserUserName"
          type="text"
          placeholder="e.g. janesmith"
          value={newUser.username}
          onChange={e => setNewUser({ ...newUser, username: e.target.value })}
          className={userNameValidator(newUser.username, true, "") && newUser.username ? "is-invalid" : ""}
        />
        {fieldError(userNameValidator, newUser.username)}
      </div>

      <div className="au-field">
        <label htmlFor="SysUserEmail">Email Address</label>
        <input
          id="SysUserEmail"
          type="email"
          placeholder="e.g. jane@example.com"
          value={newUser.email}
          onChange={e => setNewUser({ ...newUser, email: e.target.value })}
          className={emailValidator(newUser.email, true, "") && newUser.email ? "is-invalid" : ""}
        />
        {fieldError(emailValidator, newUser.email)}
      </div>

      <div className="au-field">
        <label htmlFor="SysUserPassword">Password</label>
        <div className="au-password-wrap">
          <input
            id="SysUserPassword"
            type={showPassword ? "text" : "password"}
            placeholder="Set a strong password"
            value={newUser.password}
            onChange={e => setNewUser({ ...newUser, password: e.target.value })}
            className={passwordValidator(newUser.password, true, "") && newUser.password ? "is-invalid" : ""}
          />
          <button
            type="button"
            className="au-password-toggle"
            onClick={() => setShowPassword(v => !v)}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        </div>
        {fieldError(passwordValidator, newUser.password)}
      </div>

      <div className="au-field">
        <label htmlFor="SysUserRole">User Role</label>
        <div className="au-select-wrapper">
          <select
            id="SysUserRole"
            value={newUser.role}
            onChange={e => setNewUser({ ...newUser, role: e.target.value })}
            className={selectValidator(newUser.role, true, "") && newUser.role ? "is-invalid" : ""}
          >
            <option value="">— Select a role —</option>
            <option value="admin">Admin</option>
            <option value="system user">System User</option>
          </select>
        </div>
        {fieldError(selectValidator, newUser.role)}
      </div>

      <button type="submit" className="au-submit-btn" disabled={!isFormValid()}>
        Add New User
      </button>

      <SuccessfullSubmission
        show={showSuccessModal}
        onHide={() => setShowSuccessModal(false)}
        successMessage="User Added Successfully"
      />
    </form>
  );
};
